import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';

import { ErrorService } from '../services/error.service';
import { NON_BLOCKING_ERROR_STATUSES } from './constants/error-interceptor.constants';

/**
 * HTTP Error Interceptor that handles non-200 responses
 * Redirects to error page for critical errors and provides centralized error handling
 */
export const errorInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const router = inject(Router);
  const errorService = inject(ErrorService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Skip error handling for certain URLs (like auth endpoints)
      const skipErrorHandlingUrls = ['/assets/', 'cognito-idp.eu-west-2.amazonaws.com'];

      const shouldSkipErrorHandling = skipErrorHandlingUrls.some((url: string) => req.url.includes(url));

      if (shouldSkipErrorHandling || NON_BLOCKING_ERROR_STATUSES.includes(error.status)) {
        return throwError(() => error);
      }

      // Handle different error types
      if (error.status === 0) {
        // Network error
        errorService.setError({
          status: 0,
          message: 'Network connection failed',
          details: 'Please check your internet connection and try again.',
        });
        router.navigate(['/error']);
        return throwError(() => error);
      }

      // Handle HTTP errors (400, 500, etc.)
      if (error.status >= 400) {
        const errorMessage = getErrorMessage(error);

        errorService.setError({
          status: error.status,
          message: errorMessage,
          details: error.error?.message || error.message,
        });

        // For critical errors (5xx), redirect to error page
        if (error.status >= 500) {
          router.navigate(['/error']);
        }
        // For 401, redirect to login
        else if (error.status === 401) {
          router.navigate(['/login']);
        }
        // For other 4xx errors, still set error but don't redirect
        // Components can handle these individually
      }

      return throwError(() => error);
    })
  );
};

function getErrorMessage(error: HttpErrorResponse): string {
  switch (error.status) {
    case 400:
      return 'Invalid request. Please check your input and try again.';
    case 401:
      return 'Your session has expired. Please log in again.';
    case 403:
      return "You don't have permission to access this resource.";
    case 404:
      return 'The requested resource was not found.';
    case 429:
      return 'Too many requests. Please wait and try again.';
    case 500:
      return 'Internal server error. Please try again later.';
    case 502:
      return 'Bad gateway. The server is temporarily unavailable.';
    case 503:
      return 'Service unavailable. Please try again later.';
    default:
      return `An error occurred (${error.status}). Please try again.`;
  }
}
